import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthBoundary } from './auth/AuthBoundary';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { Layout, useAllowed } from './components/Layout';
import { DashboardProvider } from './context/DashboardContext';
import type { Permission } from './types/v2';
import { V2Provider } from './v2/V2Context';
import { I18nProvider, t } from './i18n';
const OverviewPage = lazy(() => import('./pages/OverviewPage').then((module) => ({ default: module.OverviewPage })));
const ActivationPage = lazy(() => import('./pages/ActivationPage').then((module) => ({ default: module.ActivationPage })));
const RetentionPage = lazy(() => import('./pages/RetentionPage').then((module) => ({ default: module.RetentionPage })));
const EngagementPage = lazy(() => import('./pages/EngagementPage').then((module) => ({ default: module.EngagementPage })));
const AcquisitionPage = lazy(() => import('./pages/AcquisitionPage').then((module) => ({ default: module.AcquisitionPage })));
const DiagnosticsPage = lazy(() => import('./pages/DiagnosticsPage').then((module) => ({ default: module.DiagnosticsPage })));
const ReliabilityPage = lazy(() => import('./pages/ReliabilityPage').then((module) => ({ default: module.ReliabilityPage })));
const ExecutivePage = lazy(() => import('./pages/v2/ExecutivePage').then((module) => ({ default: module.ExecutivePage })));
const UsersPage = lazy(() => import('./pages/v2/UsersPage').then((module) => ({ default: module.UsersPage })));
const UserProfilePage = lazy(() => import('./pages/v2/UserProfilePage').then((module) => ({ default: module.UserProfilePage })));
const AccountActivationPage = lazy(() => import('./pages/v2/AccountCohortPages').then((module) => ({ default: module.AccountActivationPage })));
const AccountRetentionPage = lazy(() => import('./pages/v2/AccountCohortPages').then((module) => ({ default: module.AccountRetentionPage })));
const FeaturesPage = lazy(() => import('./pages/v2/ProductPages').then((module) => ({ default: module.FeaturesPage })));
const ReceiptsDetailPage = lazy(() => import('./pages/v2/ProductPages').then((module) => ({ default: module.ReceiptsDetailPage })));
const PriceSearchPage = lazy(() => import('./pages/v2/ProductPages').then((module) => ({ default: module.PriceSearchPage })));
const CostsV2Page = lazy(() => import('./pages/v2/ProductPages').then((module) => ({ default: module.CostsV2Page })));
const RetailPage = lazy(() => import('./pages/v2/RetailPage').then((module) => ({ default: module.RetailPage })));
const ExportsPage = lazy(() => import('./pages/v2/ExportsPage').then((module) => ({ default: module.ExportsPage })));

// Temporary: the diagnostics route exists only in a build made for it.
const diagnosticsTarget = import.meta.env.VITE_DIAGNOSTICS_API_URL;

/** Hides a page the role cannot open. The API refuses the data either way; this only avoids an empty page. */
function Guard({ permission, children }: { permission: Permission | Permission[]; children: ReactNode }) {
  const allowed = useAllowed();
  return allowed(permission) ? <>{children}</> : <Navigate to="/" replace />;
}

/** The commercial role has exports only, so its home is the export page. */
function Home() {
  const { role } = useAuth();
  return role === 'commercial' ? <Navigate to="/exports" replace /> : <ExecutivePage />;
}

export function AppRoutes() {
  return <Routes>
    <Route element={<Layout />}>
      <Route index element={<Home />} />
      <Route path="summary" element={<Guard permission="product.read"><OverviewPage /></Guard>} />
      <Route path="users" element={<Guard permission="users.read"><UsersPage /></Guard>} />
      <Route path="users/:key" element={<Guard permission="users.read"><UserProfilePage /></Guard>} />
      <Route path="acquisition" element={<Guard permission="product.read"><AcquisitionPage /></Guard>} />
      <Route path="activation" element={<Guard permission="product.read"><ActivationPage /></Guard>} />
      <Route path="activation/accounts" element={<Guard permission="users.read"><AccountActivationPage /></Guard>} />
      <Route path="engagement" element={<Guard permission="product.read"><EngagementPage /></Guard>} />
      <Route path="retention" element={<Guard permission="product.read"><RetentionPage /></Guard>} />
      <Route path="retention/accounts" element={<Guard permission="users.read"><AccountRetentionPage /></Guard>} />
      <Route path="features" element={<Guard permission="product.read"><FeaturesPage /></Guard>} />
      <Route path="receipts" element={<Guard permission="product.read"><ReceiptsDetailPage /></Guard>} />
      <Route path="price-search" element={<Guard permission="product.read"><PriceSearchPage /></Guard>} />
      <Route path="price-comparison" element={<Navigate to="/price-search" replace />} />
      <Route path="retail" element={<Guard permission="retail.read"><RetailPage /></Guard>} />
      <Route path="exports" element={<Guard permission={['exports.internal', 'exports.commercial']}><ExportsPage /></Guard>} />
      <Route path="reliability" element={<Guard permission="product.read"><ReliabilityPage /></Guard>} />
      <Route path="costs" element={<Guard permission="product.read"><CostsV2Page /></Guard>} />
      {diagnosticsTarget && <Route path="diagnostics" element={<DiagnosticsPage targetUrl={diagnosticsTarget} />} />}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>
  </Routes>;
}

export function App() {
  return <I18nProvider><AuthProvider><AuthBoundary><V2Provider><DashboardProvider><BrowserRouter><Suspense fallback={<div className="route-loading"><div className="spinner" aria-label={t('common.loadingPage')} /></div>}>
    <AppRoutes />
  </Suspense></BrowserRouter></DashboardProvider></V2Provider></AuthBoundary></AuthProvider></I18nProvider>;
}
